// src/pages/user/OrderDetails.js
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  FiCalendar,
  FiChevronLeft,
  FiCreditCard,
  FiPackage,
  FiMapPin,
  FiUser,
  FiDownload,
  FiDollarSign,
  FiTruck,
} from "../../../../assets/icons/vander";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ScrollToTop from "../../../../components/scroll-to-top";
import Switcher from "../../../../components/switcher";
import Usertab from "../../../../components/user-tab";

export default function OrderDetails() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const BASE_MEDIA_URL = process.env.REACT_APP_MEDIA_BASE_URL;

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/get-orders/?order_id=${orderId}`
      );

      // The API returns the order data directly, not wrapped in an array
      if (response.data) {
        setOrder(response.data);
      } else {
        setError("Order not found");
      }
      setLoading(false);
    } catch (err) {
      // Axios wraps errors in a response object
      const errorMessage = err.response
        ? err.response.data.message || err.response.statusText
        : err.message;
      setError(errorMessage);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const downloadInvoice = async (e) => {
    e.preventDefault(); // stop the default <a> navigation
    const url = BASE_MEDIA_URL + order.invoice_pdf;

    try {
      const res = await fetch(url, {
        // if the endpoint needs auth, add it here:
        // headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob(); // PDF as binary
      const objectUrl = window.URL.createObjectURL(blob);

      // create a hidden <a> that triggers the download
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `Invoice_Order_${order.order_id}.pdf`; // <-- file name
      document.body.appendChild(a);
      a.click();

      // clean-up
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error(err);
      alert("Failed to download invoice. Please try again.");
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "order placed":
        return "bg-blue-100 text-blue-800";
      case "order confirmed":
        return "bg-green-100 text-green-800";
      case "ready for shipment":
        return "bg-purple-100 text-purple-800";
      case "shipped":
        return "bg-indigo-100 text-indigo-800";
      case "in-transit":
        return "bg-teal-100 text-teal-800";
      case "out for delivery":
        return "bg-orange-100 text-orange-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "refunded proceeded":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPaymentMethodDisplay = (paymentMethod) => {
    if (paymentMethod && paymentMethod.toLowerCase() === "cod") {
      return {
        text: "Cash on Delivery",
        colorClass: "text-orange-600 dark:text-orange-400",
      };
    } else {
      return {
        text: "Online",
        colorClass: "text-blue-600 dark:text-blue-400",
      };
    }
  };

  if (loading) {
    return (
      <>
        <Navbar navClass="defaultscroll is-sticky" />

        <section className="relative lg:pb-24 pb-16 md:mt-[84px] mt-[70px]">
          <div className="md:container container-fluid relative">
            <div className="relative overflow-hidden md:rounded-md shadow dark:shadow-gray-700 h-52 bg-[url('../../assets/images/hero/pages.jpg')] bg-center bg-no-repeat bg-cover"></div>
          </div>

          <div className="container relative md:mt-24 mt-16">
            <div className="md:flex">
              <Usertab />

              <div className="lg:w-3/4 md:w-2/3 md:px-3 mt-6 md:mt-0">
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <Switcher />
        <ScrollToTop />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar navClass="defaultscroll is-sticky" />

        <section className="relative lg:pb-24 pb-16 md:mt-[84px] mt-[70px]">
          <div className="md:container container-fluid relative">
            <div className="relative overflow-hidden md:rounded-md shadow dark:shadow-gray-700 h-52 bg-[url('../../assets/images/hero/pages.jpg')] bg-center bg-no-repeat bg-cover"></div>
          </div>

          <div className="container relative md:mt-24 mt-16">
            <div className="md:flex">
              <Usertab />

              <div className="lg:w-3/4 md:w-2/3 md:px-3 mt-6 md:mt-0">
                <div className="bg-red-50 text-red-700 p-4 rounded-lg">
                  Error: {error}
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <Switcher />
        <ScrollToTop />
      </>
    );
  }

  if (!order) {
    return (
      <>
        <Navbar navClass="defaultscroll is-sticky" />

        <section className="relative lg:pb-24 pb-16 md:mt-[84px] mt-[70px]">
          <div className="md:container container-fluid relative">
            <div className="relative overflow-hidden md:rounded-md shadow dark:shadow-gray-700 h-52 bg-[url('../../assets/images/hero/pages.jpg')] bg-center bg-no-repeat bg-cover"></div>
          </div>

          <div className="container relative md:mt-24 mt-16">
            <div className="md:flex">
              <Usertab />

              <div className="lg:w-3/4 md:w-2/3 md:px-3 mt-6 md:mt-0">
                <div className="bg-gray-50 p-8 rounded-lg text-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    Order not found
                  </h3>
                  <p className="mt-2 text-gray-500">
                    The order you're looking for doesn't exist.
                  </p>
                  <Link
                    to="/user-account"
                    className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-500 hover:bg-green-600"
                  >
                    Back to Orders
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <Switcher />
        <ScrollToTop />
      </>
    );
  }

  const paymentMethod = getPaymentMethodDisplay(order.payment?.payment_method);
  const latestStatus =
    order.status_history && order.status_history.length > 0
      ? order.status_history[order.status_history.length - 1].status
      : "Unknown";

  return (
    <>
      <Navbar navClass="defaultscroll is-sticky" />

      <section className="relative lg:pb-24 pb-16 md:mt-[84px] mt-[70px]">
        <div className="md:container container-fluid relative">
          <div className="relative overflow-hidden md:rounded-md shadow dark:shadow-gray-700 h-52 bg-[url('../../assets/images/hero/pages.jpg')] bg-center bg-no-repeat bg-cover"></div>
        </div>

        <div className="container relative md:mt-24 mt-16">
          <div className="md:flex">
            <Usertab />

            <div className="lg:w-3/4 md:w-2/3 md:px-3 mt-6 md:mt-0 mb-4">
              <div className="flex items-center mb-6">
                <Link
                  to="/user-account"
                  className="flex items-center text-green-600 hover:text-green-700 mr-4"
                >
                  <FiChevronLeft className="mr-1" />
                  Back to Orders
                </Link>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Order Details
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Order Information */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Order Header */}
                  <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6">
                    <div className="flex flex-wrap justify-between items-start mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Order #{order.order_id}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Placed on {formatDate(order.order_date)}
                        </p>
                        {order.invoice_no && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Invoice No: {order.invoice_no}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            latestStatus
                          )}`}
                        >
                          {latestStatus}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          Order Date
                        </p>
                        <div className="flex items-center text-sm font-medium text-gray-900 dark:text-white">
                          <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                          {formatDate(order.order_date)}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          Expected Delivery
                        </p>
                        <div className="flex items-center text-sm font-medium text-gray-900 dark:text-white">
                          <FiTruck className="mr-2 h-4 w-4 text-gray-400" />
                          {formatDate(order.expected_delivery_date)}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          Payment Status
                        </p>
                        <div className="flex items-center text-sm font-medium text-gray-900 dark:text-white">
                          <FiCreditCard className="mr-2 h-4 w-4 text-gray-400" />
                          {order.payment?.payment_status || "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-4">
                    {/* <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Order Items
                    </h3> */}
                    <div className="space-y-4">
                      {order.items.map((item) => (
                        <div
                          key={item.order_item_id}
                          className="flex flex-col md:flex-row border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                        >
                          <div className="md:w-1/4 mb-4 md:mb-0">
                            <img
                              src={BASE_MEDIA_URL + item.main_image}
                              alt={item.product_name}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-3/4 md:pl-6">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="text-base font-medium text-gray-900 dark:text-white">
                                  {item.product_name}
                                </h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {item.brand?.name} | {item.variant?.gender} |{" "}
                                  {item.variant?.fabric}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-base font-medium text-gray-900 dark:text-white">
                                  ₹
                                  {item.variant?.sizes?.[0]?.price
                                    ?.discounted || "0.00"}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  Qty: {item.order_quantity}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center mt-2">
                              <span
                                className="inline-block w-4 h-4 rounded-full mr-2 border border-gray-300"
                                style={{
                                  backgroundColor:
                                    item.variant?.color_code || "#CCCCCC",
                                }}
                              ></span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {item.variant?.color || "N/A"} | Size:{" "}
                                {item.variant?.sizes?.[0]?.size || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Shipping Address - Moved here */}
                  <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-4">
                    {/* <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Shipping Address
                    </h3> */}
                    <div className="flex items-start">
                      <FiMapPin className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {order.shipping_address?.full_name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {order.shipping_address?.address1},{" "}
                          {order.shipping_address?.address2},{" "}
                        
                          {order.shipping_address?.landmark},{" "}
                          {order.shipping_address?.town}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {order.shipping_address?.district},{" "}
                          {order.shipping_address?.state} -{" "}
                          {order.shipping_address?.pincode}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Phone: {order.shipping_address?.mobile_no}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Order Status Timeline */}
                  <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-4">
                    {/* <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Order Status
                    </h3> */}
                    <div className="space-y-4">
                      {order.status_history.map((status, index) => (
                        <div key={status.history_id} className="flex">
                          <div className="flex flex-col items-center mr-4">
                            <div
                              className={`w-3 h-3 rounded-full ${
                                index === order.status_history.length - 1
                                  ? "bg-green-500"
                                  : "bg-gray-300"
                              }`}
                            ></div>
                            {index < order.status_history.length - 1 && (
                              <div className="w-0.5 h-10 bg-gray-300 mt-1"></div>
                            )}
                          </div>
                          <div className="pb-4">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {status.status}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(status.updated_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column - Summary and Actions */}
                <div className="space-y-6">
                  {/* Order Summary */}
                  <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Order Summary
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Subtotal
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          ₹{order.total_amount}
                        </p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Shipping
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          Free
                        </p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Tax
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          ₹0.00
                        </p>
                      </div>
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                        <div className="flex justify-between">
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            Total
                          </p>
                          <p className="text-base font-semibold text-gray-900 dark:text-white">
                            ₹{order.total_amount}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Download Invoice Button */}
                    {order.invoice_pdf && (
                      <div className="mt-6">
                        <button
                          onClick={downloadInvoice}
                          className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                        >
                          <FiDownload className="mr-2 h-4 w-4" />
                          Download Invoice
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Payment Information */}
                  <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Payment Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <FiCreditCard className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Payment Method
                          </p>
                          <p
                            className={`text-sm font-medium ${paymentMethod.colorClass}`}
                          >
                            {paymentMethod.text}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FiDollarSign className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Payment Status
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {order.payment?.payment_status || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FiDollarSign className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Payment Amount
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            ₹{order.payment?.payment_amount || "0.00"}
                          </p>
                        </div>
                      </div>
                      {order.payment?.payment_date && (
                        <div className="flex items-center">
                          <FiCalendar className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Payment Date
                            </p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatDate(order.payment.payment_date)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer Information */}
                  <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Customer Information
                    </h3>
                    <div className="flex items-start">
                      <FiUser className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {order.customer?.first_name}{" "}
                          {order.customer?.last_name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {order.customer?.email}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Phone: {order.customer?.mobile_no}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <Switcher />
        <ScrollToTop />
      </section>
    </>
  );
}
